import React, { useState, useRef, useEffect } from 'react';

/**
 * ProgressiveImage — Lazy loads images with IntersectionObserver + blur-up placeholder.
 * - Starts with a low-quality blurred placeholder
 * - Loads the full image when it enters the viewport
 * - Fades in when loaded
 * - Supports responsive srcSet for Unsplash AND ZenStudios CDN images
 *
 * ZenStudios CDN thumbnails follow the pattern:
 *   original: https://cdn.zenstudios.in/listings/…/abc.webp
 *   small:    https://cdn.zenstudios.in/listings/…/abc_small.webp
 *   medium:   https://cdn.zenstudios.in/listings/…/abc_medium.webp
 *   large:    https://cdn.zenstudios.in/listings/…/abc_large.webp
 *
 * You can pass `thumbnails` directly ({ small, medium, large, original })
 * or let the component derive them from `src`.
 */
export default function ProgressiveImage({
  src,
  alt = '',
  className = '',
  placeholderColor = '#1e293b',
  width,
  height,
  onClick,
  sizes,
  style,
  thumbnails,
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(el);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Build responsive srcSet from thumbnails or by URL pattern
  const getSrcSet = () => {
    // 1. Explicit thumbnails object (from MongoDB)
    if (thumbnails) {
      const parts = [];
      if (thumbnails.small) parts.push(`${thumbnails.small} 400w`);
      if (thumbnails.medium) parts.push(`${thumbnails.medium} 800w`);
      if (thumbnails.large) parts.push(`${thumbnails.large} 1200w`);
      return parts.length ? parts.join(', ') : undefined;
    }

    if (!src) return undefined;

    // 2. ZenStudios CDN image — derive thumbnail URLs from base key
    if (src.includes('cdn') && src.includes('zenstudios') && src.includes('.webp')) {
      const base = src.replace(/\.webp$/, '');
      return [
        `${base}_small.webp 400w`,
        `${base}_medium.webp 800w`,
        `${base}_large.webp 1200w`,
      ].join(', ');
    }

    // 3. Unsplash images
    if (src.includes('unsplash.com')) {
      const base = src.split('?')[0];
      return [
        `${base}?auto=format&w=400&q=60 400w`,
        `${base}?auto=format&w=800&q=70 800w`,
        `${base}?auto=format&w=1200&q=80 1200w`,
      ].join(', ');
    }

    return undefined;
  };

  // Tiny blur placeholder URL
  const getPlaceholder = () => {
    if (!src) return undefined;
    if (src.includes('unsplash.com')) {
      const base = src.split('?')[0];
      return `${base}?auto=format&w=20&q=10`;
    }
    // For CDN images, use small thumbnail as placeholder
    if (thumbnails?.small) return thumbnails.small;
    return undefined;
  };

  const placeholder = getPlaceholder();
  const srcSet = getSrcSet();

  // Determine display src
  const displaySrc = (() => {
    if (!src) return '';
    if (src.includes('unsplash.com')) {
      return `${src.split('?')[0]}?auto=format&w=1200&q=80`;
    }
    return thumbnails?.original || src;
  })();

  return (
    <div
      ref={imgRef}
      className={`relative overflow-hidden ${className}`}
      style={{ backgroundColor: placeholderColor, ...style }}
      onClick={onClick}
      data-testid="progressive-image"
      {...rest}
    >
      {/* Blur placeholder */}
      {placeholder && !loaded && (
        <img
          src={placeholder}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'blur(20px)', transform: 'scale(1.1)' }}
        />
      )}

      {/* Full image — only load when in viewport */}
      {inView && src && !hasError && (
        <img
          src={displaySrc}
          srcSet={srcSet}
          sizes={sizes || '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw'}
          alt={alt}
          width={width}
          height={height}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => { setHasError(true); setLoaded(true); }}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}

      {/* Fallback for broken images */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800/50">
          <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
          </svg>
        </div>
      )}
    </div>
  );
}

/**
 * ProgressiveGallery — A lazy-loading image gallery with blur-up placeholders
 */
export function ProgressiveGallery({
  images = [],
  selectedIndex = 0,
  onSelect,
  className = '',
}) {
  if (!images.length) return null;

  return (
    <div className={`flex gap-2 overflow-x-auto ${className}`} data-testid="progressive-gallery">
      {images.map((img, idx) => {
        const url = typeof img === 'string' ? img : (img?.url || img?.original || img?.cdn_url || '');
        if (!url) return null;
        return (
          <button
            key={idx}
            onClick={() => onSelect?.(idx)}
            className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all ${idx === selectedIndex ? 'border-amber-500' : 'border-transparent opacity-70 hover:opacity-100'}`}
            data-testid={`gallery-thumb-${idx}`}
          >
            <ProgressiveImage
              src={url}
              alt={`Gallery image ${idx + 1}`}
              className="w-full h-full"
            />
          </button>
        );
      })}
    </div>
  );
}
